import type {
  PlayLogEntry,
  PlayLogEvent,
  PlayLogJsonLineEntry,
  PlayLogSample,
  PlayLogSession,
  PlayLogSessionEndEntry,
  PlayLogSessionStartEntry,
  PlayLogSnapshotSummary,
} from "./playLogTypes";

export interface CreatePlayLogRecorderOptions {
  readonly runId: string;
}

export interface StartPlayLogSessionInput {
  readonly startedAtMs: number;
  readonly label?: string;
  readonly purpose?: string;
  readonly initialSpeedMultiplier: number;
}

export interface StopPlayLogSessionInput {
  readonly endedAtMs: number;
}

export type RecordPlayLogEventInput = Omit<
  PlayLogEvent,
  "kind" | "runId" | "seq"
>;

export type RecordPlayLogSampleInput = Omit<
  PlayLogSample,
  "kind" | "runId" | "seq"
>;

export interface PlayLogRecorder {
  startSession(input: StartPlayLogSessionInput): PlayLogSession;
  recordEvent(input: RecordPlayLogEventInput): PlayLogEvent;
  recordSample(input: RecordPlayLogSampleInput): PlayLogSample;
  stopSession(input: StopPlayLogSessionInput): PlayLogSessionEndEntry;
  exportJsonLines(): string;
  clear(): void;
  getEntries(): readonly PlayLogEntry[];
  getSession(): PlayLogSession | undefined;
  getSessionEnd(): PlayLogSessionEndEntry | undefined;
}

export function createPlayLogRecorder(
  options: CreatePlayLogRecorderOptions,
): PlayLogRecorder {
  return new InMemoryPlayLogRecorder(options);
}

class InMemoryPlayLogRecorder implements PlayLogRecorder {
  private readonly runId: string;
  private entries: PlayLogEntry[] = [];
  private nextSeq = 1;
  private session: PlayLogSession | undefined;
  private sessionEnd: PlayLogSessionEndEntry | undefined;

  constructor(options: CreatePlayLogRecorderOptions) {
    assertNonEmptyString("runId", options.runId);
    this.runId = options.runId;
  }

  startSession(input: StartPlayLogSessionInput): PlayLogSession {
    if (this.sessionEnd !== undefined) {
      throw new Error(
        "PlayLog session has ended. Call clear() before restart.",
      );
    }
    if (this.session !== undefined) {
      throw new Error("PlayLog session is already active.");
    }

    assertNonNegativeFinite("startedAtMs", input.startedAtMs);
    assertNonNegativeFinite(
      "initialSpeedMultiplier",
      input.initialSpeedMultiplier,
    );

    const session: PlayLogSession = {
      runId: this.runId,
      startedAtMs: input.startedAtMs,
      initialSpeedMultiplier: input.initialSpeedMultiplier,
      ...(input.label !== undefined ? { label: input.label } : {}),
      ...(input.purpose !== undefined ? { purpose: input.purpose } : {}),
    };
    this.session = session;

    return cloneSession(session);
  }

  recordEvent(input: RecordPlayLogEventInput): PlayLogEvent {
    const session = this.requireActiveSession();
    assertCommonEntryInput(input);
    assertNonEmptyString("type", input.type);

    const entry: PlayLogEvent = {
      kind: "event",
      runId: session.runId,
      seq: this.nextSeq,
      wallTimeMs: input.wallTimeMs,
      gameTimeMs: input.gameTimeMs,
      speedMultiplier: input.speedMultiplier,
      type: input.type,
      ...(input.payload !== undefined
        ? { payload: cloneRecord(input.payload) }
        : {}),
      ...(input.snapshot !== undefined
        ? { snapshot: cloneSnapshotSummary(input.snapshot) }
        : {}),
    };
    this.nextSeq += 1;
    this.entries = [...this.entries, entry];

    return cloneEventEntry(entry);
  }

  recordSample(input: RecordPlayLogSampleInput): PlayLogSample {
    const session = this.requireActiveSession();
    assertCommonEntryInput(input);

    const entry: PlayLogSample = {
      kind: "sample",
      runId: session.runId,
      seq: this.nextSeq,
      wallTimeMs: input.wallTimeMs,
      gameTimeMs: input.gameTimeMs,
      speedMultiplier: input.speedMultiplier,
      snapshot: cloneSnapshotSummary(input.snapshot),
    };
    this.nextSeq += 1;
    this.entries = [...this.entries, entry];

    return cloneSampleEntry(entry);
  }

  stopSession(input: StopPlayLogSessionInput): PlayLogSessionEndEntry {
    const session = this.requireStartedSession();
    if (this.sessionEnd !== undefined) {
      throw new Error("PlayLog session has ended.");
    }
    assertNonNegativeFinite("endedAtMs", input.endedAtMs);

    this.sessionEnd = {
      kind: "session-end",
      runId: session.runId,
      endedAtMs: input.endedAtMs,
    };

    return { ...this.sessionEnd };
  }

  exportJsonLines(): string {
    if (this.session === undefined) {
      return "";
    }

    const lines: PlayLogJsonLineEntry[] = [
      toSessionStartEntry(this.session),
      ...this.entries,
    ];
    if (this.sessionEnd !== undefined) {
      lines.push(this.sessionEnd);
    }

    return lines.map((line) => JSON.stringify(line)).join("\n");
  }

  clear(): void {
    this.entries = [];
    this.nextSeq = 1;
    this.session = undefined;
    this.sessionEnd = undefined;
  }

  getEntries(): readonly PlayLogEntry[] {
    return this.entries.map((entry) => cloneEntry(entry));
  }

  getSession(): PlayLogSession | undefined {
    if (this.session === undefined) {
      return undefined;
    }

    return cloneSession(this.session);
  }

  getSessionEnd(): PlayLogSessionEndEntry | undefined {
    if (this.sessionEnd === undefined) {
      return undefined;
    }

    return { ...this.sessionEnd };
  }

  private requireStartedSession(): PlayLogSession {
    if (this.session === undefined) {
      throw new Error("PlayLog session has not started.");
    }

    return this.session;
  }

  private requireActiveSession(): PlayLogSession {
    const session = this.requireStartedSession();
    if (this.sessionEnd !== undefined) {
      throw new Error("PlayLog session has ended. Call clear() before append.");
    }

    return session;
  }
}

function toSessionStartEntry(
  session: PlayLogSession,
): PlayLogSessionStartEntry {
  return {
    kind: "session-start",
    ...cloneSession(session),
  };
}

function cloneSession(session: PlayLogSession): PlayLogSession {
  return {
    runId: session.runId,
    startedAtMs: session.startedAtMs,
    initialSpeedMultiplier: session.initialSpeedMultiplier,
    ...(session.label !== undefined ? { label: session.label } : {}),
    ...(session.purpose !== undefined ? { purpose: session.purpose } : {}),
  };
}

function cloneEntry(entry: PlayLogEntry): PlayLogEntry {
  if (entry.kind === "event") {
    return cloneEventEntry(entry);
  }

  return cloneSampleEntry(entry);
}

function cloneEventEntry(entry: PlayLogEvent): PlayLogEvent {
  return {
    kind: "event",
    runId: entry.runId,
    seq: entry.seq,
    wallTimeMs: entry.wallTimeMs,
    gameTimeMs: entry.gameTimeMs,
    speedMultiplier: entry.speedMultiplier,
    type: entry.type,
    ...(entry.payload !== undefined
      ? { payload: cloneRecord(entry.payload) }
      : {}),
    ...(entry.snapshot !== undefined
      ? { snapshot: cloneSnapshotSummary(entry.snapshot) }
      : {}),
  };
}

function cloneSampleEntry(entry: PlayLogSample): PlayLogSample {
  return {
    kind: "sample",
    runId: entry.runId,
    seq: entry.seq,
    wallTimeMs: entry.wallTimeMs,
    gameTimeMs: entry.gameTimeMs,
    speedMultiplier: entry.speedMultiplier,
    snapshot: cloneSnapshotSummary(entry.snapshot),
  };
}

function cloneSnapshotSummary(
  snapshot: PlayLogSnapshotSummary,
): PlayLogSnapshotSummary {
  return {
    ...snapshot,
    ...(snapshot.nHovBranchLevels !== undefined
      ? { nHovBranchLevels: { ...snapshot.nHovBranchLevels } }
      : {}),
  };
}

function cloneRecord(record: Record<string, unknown>): Record<string, unknown> {
  return { ...record };
}

function assertCommonEntryInput(input: {
  readonly wallTimeMs: number;
  readonly gameTimeMs: number;
  readonly speedMultiplier: number;
}): void {
  assertNonNegativeFinite("wallTimeMs", input.wallTimeMs);
  assertNonNegativeFinite("gameTimeMs", input.gameTimeMs);
  assertNonNegativeFinite("speedMultiplier", input.speedMultiplier);
}

function assertNonNegativeFinite(name: string, value: number): void {
  if (!Number.isFinite(value) || value < 0) {
    throw new Error(`PlayLog ${name} must be a non-negative finite number.`);
  }
}

function assertNonEmptyString(name: string, value: string): void {
  if (value.trim().length === 0) {
    throw new Error(`PlayLog ${name} must be a non-empty string.`);
  }
}
